import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { parseISO, compareAsc, format, startOfDay } from 'date-fns';
import { TranslationService } from '@examsoft/translation';

@Component({
  selector: 'summary-form',
  templateUrl: './summary-form.component.html',
  styleUrls: ['./summary-form.component.scss']
})
export class SummaryFormComponent{
  @Input() institutionId;
  @Input() institutionName;
  @Input() user;
  @Input() authType;
  @Input() appType;

  @Input() usagePlansDropDown;
  @Input() dataCount;
  @Output() onDateRange = new EventEmitter();
  @Output() onSave = new EventEmitter();
  @Input() status: 'loading'|'save';
  public usageDatePlans = [];
  @Input() planData;


  public editForm: 'save'|'update'|null;

  private label 

  constructor(
    private fb: FormBuilder,
    private translationService:TranslationService
  ){
    this.label = {
      'exam-id': {
        perPeriod: this.translationService.get('Users per period'),
        perUnit: this.translationService.get('Price per user')
      },
      'exam-monitor': {
        perPeriod: this.translationService.get('Units per period'),
        perUnit: this.translationService.get('Price per unit')
      }
    }
  }

  ngOnChanges(changes) {
    if(changes.status){
      this.status = changes.status.currentValue;
    }

    if(changes.usagePlansDropDown){
      this.usagePlansDropDown = changes.usagePlansDropDown.currentValue;
      this.usageDatePlans = [];
      this.setup();
    }
  }

  

  public getLabel(label){
    return this.label[this.appType][label];
  }

  public validateForm(){   
    return this.formData.dirty && this.formData.status === 'VALID';
  }

  formData = this.fb.group({
    usagePlanId: [''],
    instituteId: [''],
    instituteName: [''],
    productType: [''],
    overagePrice: [''],
    user: [''],
    billingStartDate: ['', Validators.required],
    billingEndDate: ['', Validators.required],
    effectiveStartDate:['', Validators.required],
    effectiveEndDate: ['', Validators.required],
    units: ['', Validators.required],
    unitPrice: ['', Validators.required],
  }, {  validators: Validators.required })


  public viewType: 'view_admin'|'view_institute';

  private setup(){
    this.setupPlanUsageData();
    if( this.authType === 'internal' ){
      this.viewType =  'view_admin'
      if( !this.usagePlansDropDown.length ){
        this.editForm = 'save';
      }
      this.setupForm();
    }else{
      this.viewType =  'view_institute';
    }
  }

  ngOnInit(){
    this.setup();
  }

  public toggleEdit(type){
    this.editForm = type;
    this.setupForm();
    let data={};
    if( this.editForm === 'update' ){
      this.planData =  this.getSelectedPlan();
      let { startFormat, endFormat} = this.getBillingDate();

      data = {
        usagePlanId: this.planData.usagePlanId,
        units: this.planData['units'],
        unitPrice:  this.planData['unitPrice'],
        overagePrice:  this.planData['overagePrice'],
        effectiveStartDate: startFormat + ' 00:00:00 000',
        effectiveEndDate: endFormat + ' 23:59:59 000',
        billingStartDate: startFormat,
        billingEndDate: endFormat,
      };

      this.formData.patchValue(data);
    }else if(this.editForm === 'save'){
      data = this.planData = {
        units: 0,
        unitPrice: 0,
        billingStartDate: null,
        billingEndDate: null,
      };

      for(const key in data) {
        this.setInitialFormDataOnSave(key, data[key])
      } 
    }
  }

  private setInitialFormDataOnSave(name, data){
    this.formData.controls[name].setValue(data);
    name !== 'unitPrice' && this.formData.controls[name].setErrors({onlySelf: true});
  }

  private dateF = 'yyyy-MM-dd';

  public getDate(type: 'billingStartDate'|'billingEndDate'){
    let date = this.formData.get(type).value;
    return date && parseISO(date);
  }

  public onDropDownRange(event){
    this.usageDatePlans.forEach( ( data, i ) => {
      data.selected = event.detail.uid === i
    });

    let data = this.usageDatePlans[event.detail.uid];
    let date = data.name.split(' - ');
    let startdate = date[0];
    let enddate = date[1];
    this.onDateRange.emit({ startdate, enddate });
  }

  public onRangeSelected(event){
    this.onDateRange.emit(event);
  }

  getSelectedPlan(){
    let plan = this.usageDatePlans.filter( plan => plan.selected )[0]
    let data = this.usagePlansDropDown[plan.uid];
    return data;
  }

  public getBillingDate(){
    let currentDate = this.planData.billingPeriod;
    let date = currentDate.split(' - ');
    let start = date[0];
    let end = date[1];

    let startFormat = format(parseISO(start), this.dateF);
    let endFormat = format(parseISO(end), this.dateF);
    return { start, end, startFormat, endFormat };
  }

  private setupForm(){
    this.formData.markAsPristine();
    if(this.authType === 'internal'){
      let data = {};
      let result = {
        instituteId: this.institutionId,
        instituteName: this.institutionName,
        productType: this.appType,
        user: this.user,
        billingStartDate: null,
        billingEndDate: null,
      };

      this.formData.patchValue({ ...result, ...data });
    }
  }

  private formUpdate(name, data){
    const formControl = this.formData.controls[name];
    const isUnitPriceZero = name === 'unitPrice' && data === 0;

    if (data || isUnitPriceZero) {
      formControl.setValue(data);
      formControl.markAsDirty();
    } else {
      formControl.setErrors({onlySelf: true});
    }
  }

  public updateDateRangeSelected(date){
    const effectiveStartDate = date.startDate ? date.startDate + ' 00:00:00 000' : date.startDate;
    const effectiveEndDate = date.endDate ? date.endDate + ' 23:59:59 000' : date.endDate;

    const params = { 
      'billingStartDate': date.startDate, 
      'billingEndDate': date.endDate, 
      'effectiveStartDate': effectiveStartDate, 
      'effectiveEndDate' : effectiveEndDate,
    };

    for(const key in params) {
      this.formUpdate(key, params[key])
    }    
  }

  public updateUnitsPerPeriod(event){
    if(event.detail){
      this.formUpdate('units', event.detail.value );
    }
  }

  public updatePricePerUnit(event){
    if(event.detail){
      this.formUpdate('unitPrice', event.detail.value );
    }
  }

  private setupPlanUsageData(){
    let data =  this.usagePlansDropDown;
    let selectedItems = this.planData;
    if(data.length){
      this.usageDatePlans = data.map( (usage, i) => {
        let current;
        if( selectedItems && selectedItems.usagePlanId === usage.usagePlanId){
          current = true;
        }else{
          current = 0;
        }

        let date = usage.billingPeriod.split(' - ');
        let start = format(parseISO(date[0]), 'MMM dd, yyyy');
        let end = format(parseISO(date[1]), 'MMM dd, yyyy');
        let billingPeriod = `${start} - ${end}`;

        return   {
          uid: i,
          name: billingPeriod,
          selected: current,
          usagePlanId: usage.usagePlanId
        }
      });
    }
  }

  private findSelectedPlanUsageData(){
    return this.usageDatePlans.find( data => data.selected );
  }

  public onSaveForm(){
    this.status = 'loading';
    let data = this.formData.getRawValue();
    this.onSave.emit({ saveType: this.editForm, data })
    this.editForm = null;
  }

  public onDeleteForm(){
    let saveType = 'delete'
    let data = this.formData.getRawValue();
    this.onSave.emit({ saveType, data })
    this.editForm = null;
  }

  public onCancel(){
    this.editForm = null;
    this.formData.markAsPristine();
  }
}

